import { type ReactNode } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  BarChart3,
  BookOpen,
  Building2,
  LogOut,
  Megaphone,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import { Button, Flex, HStack, Icon, Text, VStack } from "@chakra-ui/react";
import { AppRoutes } from "../../config/app.routes";
import { useAuth } from "../../services/auth/AuthProvider";

interface NavEntry {
  href: AppRoutes;
  icon: LucideIcon;
  label: string;
}

const NAV: NavEntry[] = [
  { href: AppRoutes.Books, icon: BookOpen, label: "Книги" },
  { href: AppRoutes.Statistics, icon: BarChart3, label: "Статистика" },
  { href: AppRoutes.Campaigns, icon: Megaphone, label: "Промо-кампанії" },
];

const SUPERADMIN_NAV: NavEntry[] = [
  { href: AppRoutes.Balance, icon: Wallet, label: "Баланс" },
  { href: AppRoutes.Publishers, icon: Building2, label: "Видавництва" },
];

export function AppSidebar({
  publishingHouseName,
  isSuperadmin,
  onNavigate,
  children,
}: {
  publishingHouseName: string;
  isSuperadmin: boolean;
  /** Called when the user picks an entry — lets the mobile drawer close itself. */
  onNavigate?: () => void;
  children?: ReactNode;
}) {
  const { pathname } = useLocation();
  const { logout } = useAuth();

  const items = [...NAV, ...(isSuperadmin ? SUPERADMIN_NAV : [])];

  return (
    <Flex
      as="aside"
      direction="column"
      w={{ base: "full", md: "64" }}
      flexShrink={0}
      h="full"
      bg="brand.charcoal"
      color="white"
      overflowX="hidden"
    >
      <VStack
        spacing={3}
        align="center"
        p={6}
        borderBottom="1px solid"
        borderColor="whiteAlpha.200"
      >
        <Text
          color="brand.orange"
          fontSize="xl"
          fontWeight="medium"
          letterSpacing="0.2em"
          textTransform="uppercase"
        >
          Читанка
        </Text>
        {children}
        <Text fontSize="sm" fontWeight="medium" opacity={0.8} textAlign="center">
          {publishingHouseName}
        </Text>
      </VStack>

      <VStack
        as="nav"
        spacing={1}
        align="stretch"
        flex={1}
        overflowY="auto"
        overflowX="hidden"
        p={3}
      >
        {items.map(({ href, icon, label }) => {
          const active = pathname === href;
          return (
            <RouterLink
              key={href}
              to={href}
              style={{ textDecoration: "none" }}
              onClick={onNavigate}
            >
              <HStack
                spacing={3}
                px={4}
                py={{ base: 3, md: 2.5 }}
                minW={0}
                borderRadius="full"
                fontSize="sm"
                fontWeight="medium"
                letterSpacing="wide"
                textTransform="uppercase"
                transition="background 0.15s, opacity 0.15s"
                bg={active ? "brand.orange" : "transparent"}
                color="white"
                opacity={active ? 1 : 0.7}
                _hover={{
                  bg: active ? "brand.orange" : "whiteAlpha.200",
                  opacity: 1,
                }}
              >
                <Icon as={icon} boxSize={4} flexShrink={0} />
                <Text as="span" noOfLines={1}>
                  {label}
                </Text>
              </HStack>
            </RouterLink>
          );
        })}
      </VStack>

      <VStack
        spacing={2}
        align="stretch"
        p={3}
        borderTop="1px solid"
        borderColor="whiteAlpha.200"
      >
        <Button
          variant="ghost"
          justifyContent="flex-start"
          gap={3}
          color="white"
          opacity={0.7}
          _hover={{ bg: "whiteAlpha.200", opacity: 1 }}
          onClick={logout}
        >
          <Icon as={LogOut} boxSize={4} />
          Вийти
        </Button>
      </VStack>
    </Flex>
  );
}
